/**
 * useNotifications — single source of truth for the notification system.
 *
 * Holds a flat array of notifications. Unread count is always computed,
 * never stored separately. Every mark-as-read function does an optimistic
 * local update first, then persists to the API (fire-and-forget).
 *
 * "Born as read" pattern: when a DM notification arrives via WebSocket
 * for the conversation the user is currently viewing, it's added with
 * is_read: true so it never flashes as unread.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { isDesktopApp, desktopSetBadge, desktopNotifyDeep, DESKTOP_BANNER_TYPES } from '../lib/desktopBridge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Fire-and-forget POST with one retry. Logs on final failure instead of swallowing. */
function persistMarkRead(url, userId) {
  const fullUrl = userId ? `${url}${url.includes('?') ? '&' : '?'}user_id=${userId}` : url;
  fetch(fullUrl, { method: 'POST' }).catch(() => {
    // Retry once after 2s
    setTimeout(() => {
      fetch(fullUrl, { method: 'POST' }).catch((err) => {
        console.warn('[useNotifications] mark-read failed after retry:', fullUrl, err);
      });
    }, 2000);
  });
}

export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  // P2-k (DIV-041): the server's total unread count. The list below is only
  // the first 50 rows, so counting unread rows in it under-reports the badge.
  // null until the first fetch; then adjusted optimistically by the mark-read
  // helpers and by WebSocket arrivals (see unreadCount below).
  const [serverUnread, setServerUnread] = useState(null);
  const notificationsRef = useRef([]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  // Adjust the server total locally between fetches (only when we know it).
  const bumpServerUnread = useCallback((delta) => {
    if (!delta) return;
    setServerUnread(c => (c === null ? c : Math.max(0, c + delta)));
  }, []);
  const activeConversationRef = useRef(null);
  const userIdRef = useRef(userId);
  const offsetRef = useRef(0);
  const PAGE_SIZE = 50;

  // Track IDs that have been optimistically marked as read.
  // Prevents refetch from reverting them before the server persists.
  const optimisticReadIds = useRef(new Set());

  // Keep ref in sync
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // ──────────── Fetch (resets to first page) ────────────
  const refetch = useCallback(async () => {
    if (!userIdRef.current) return;
    try {
      offsetRef.current = 0;
      const res = await fetch(
        `${API_URL}/api/comms/v3/notifications/${userIdRef.current}?include_read=true&limit=${PAGE_SIZE}&offset=0`
      );
      if (res.ok) {
        const data = await res.json();
        const serverNotifs = data.notifications || [];
        setHasMore(!!data.has_more);
        // Merge: keep optimistic reads that the server hasn't caught up to yet
        const merged = optimisticReadIds.current.size > 0
          ? serverNotifs.map(n => optimisticReadIds.current.has(n.id) ? { ...n, is_read: true } : n)
          : serverNotifs;
        setNotifications(merged);
        if (typeof data.unread_count === 'number') {
          // Subtract optimistic reads the server has not persisted yet.
          const pendingOptimistic = serverNotifs.filter(n => !n.is_read && optimisticReadIds.current.has(n.id)).length;
          setServerUnread(Math.max(0, data.unread_count - pendingOptimistic));
        }
      }
    } catch (err) {
      console.error('[useNotifications] fetch error:', err);
    }
  }, []);

  // ──────────── Load more (appends next page) ────────────
  const loadMore = useCallback(async () => {
    if (!userIdRef.current || !hasMore) return;
    try {
      const nextOffset = offsetRef.current + PAGE_SIZE;
      const res = await fetch(
        `${API_URL}/api/comms/v3/notifications/${userIdRef.current}?include_read=true&limit=${PAGE_SIZE}&offset=${nextOffset}`
      );
      if (res.ok) {
        const data = await res.json();
        const serverNotifs = data.notifications || [];
        setHasMore(!!data.has_more);
        offsetRef.current = nextOffset;
        const merged = optimisticReadIds.current.size > 0
          ? serverNotifs.map(n => optimisticReadIds.current.has(n.id) ? { ...n, is_read: true } : n)
          : serverNotifs;
        setNotifications(prev => {
          // Deduplicate by id when appending
          const existingIds = new Set(prev.map(n => n.id));
          const newNotifs = merged.filter(n => !existingIds.has(n.id));
          return [...prev, ...newNotifs];
        });
      }
    } catch (err) {
      console.error('[useNotifications] loadMore error:', err);
    }
  }, [hasMore]);

  // Initial fetch + visibility change backstop
  useEffect(() => {
    if (!userId) return;
    refetch();

    const onVisibilityChange = () => {
      if (!document.hidden) refetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [userId, refetch]);

  // ──────────── Computed ────────────
  // Unread rows in the loaded slice are always a lower bound; the server total
  // (when known) wins so the badge matches what mobile shows.
  const sliceUnread = notifications.filter(n => !n.is_read).length;
  const unreadCount = serverUnread === null ? sliceUnread : Math.max(sliceUnread, serverUnread);

  // ──────────── Desktop app (Tauri): badge + native banners ────────────
  // No-ops in the browser. Banner fires only for NEW unread notifications
  // that arrive after the initial load, and only while the window is unfocused.
  const desktopSeenIds = useRef(null);
  useEffect(() => {
    if (!isDesktopApp()) return;
    desktopSetBadge(unreadCount);
    if (desktopSeenIds.current === null) {
      // Wait for the FIRST real load before baselining — otherwise everything
      // fetched at launch counts as "new" and floods the user with banners.
      if (notifications.length === 0) return;
      desktopSeenIds.current = new Set(notifications.map(n => n.id));
      return;
    }
    const seen = desktopSeenIds.current;
    let banners = 0;
    for (const n of notifications) {
      if (!seen.has(n.id)) {
        seen.add(n.id);
        if (!n.is_read && !document.hasFocus() && DESKTOP_BANNER_TYPES.has(n.type) && banners < 3) {
          banners += 1;
          desktopNotifyDeep((['service_request','service_response','service_updated','service_comment'].includes(n.type) && n.title) || n.sender_name || 'Worship OS', n.preview || 'New activity', JSON.stringify({
            id: n.id,
            type: n.type,
            post_id: n.post_id || null,
            comment_id: n.comment_id || null,
            conversation_id: n.conversation_id || null,
            message_id: n.message_id || null,
            workshop_url: n.workshop_url || null,
            ...(['service_request','service_response','service_updated','service_comment'].includes(n.type) ? { service_id: n.service_id || null } : {}),
          }));
        }
      }
    }
  }, [notifications, unreadCount]);

  // ──────────── Mark single ────────────
  const markRead = useCallback((id) => {
    if (notificationsRef.current.some(n => n.id === id && !n.is_read)) bumpServerUnread(-1);
    setNotifications(prev => { const next = prev.map(n => n.id === id ? { ...n, is_read: true } : n); notificationsRef.current = next; return next; });
    optimisticReadIds.current.add(id);
    setTimeout(() => optimisticReadIds.current.delete(id), 5000);
    persistMarkRead(`${API_URL}/api/comms/v3/notifications/${id}/read`, userIdRef.current);
  }, [bumpServerUnread]);

  // ──────────── Mark multiple (for grouped notifications) ────────────
  const markReadByIds = useCallback((ids) => {
    const idSet = new Set(ids);
    bumpServerUnread(-notificationsRef.current.filter(n => idSet.has(n.id) && !n.is_read).length);
    setNotifications(prev => { const next = prev.map(n => idSet.has(n.id) ? { ...n, is_read: true } : n); notificationsRef.current = next; return next; });
    ids.forEach(id => {
      optimisticReadIds.current.add(id);
      setTimeout(() => optimisticReadIds.current.delete(id), 5000);
      persistMarkRead(`${API_URL}/api/comms/v3/notifications/${id}/read`, userIdRef.current);
    });
  }, [bumpServerUnread]);

  // ──────────── Mark all ────────────
  const markAllRead = useCallback(() => {
    setServerUnread(c => (c === null ? c : 0));
    setNotifications(prev => {
      prev.forEach(n => {
        if (!n.is_read && n.id) {
          optimisticReadIds.current.add(n.id);
          setTimeout(() => optimisticReadIds.current.delete(n.id), 5000);
        }
      });
      return prev.map(n => ({ ...n, is_read: true }));
    });
    if (userIdRef.current) {
      persistMarkRead(`${API_URL}/api/comms/v3/notifications/mark-all-read/${userIdRef.current}`);
    }
  }, []);

  // ──────────── Mark by target (post, comment, or conversation) ────────────
  const markReadByTarget = useCallback((target) => {
    const { postId, commentId, conversationId } = target;
    bumpServerUnread(-notificationsRef.current.filter(n => !n.is_read && (
      (postId && n.post_id === postId) ||
      (commentId && n.comment_id === commentId) ||
      (conversationId && n.conversation_id === conversationId))).length);
    setNotifications(prev => prev.map(n => {
      if ((postId && n.post_id === postId) ||
          (commentId && n.comment_id === commentId) ||
          (conversationId && n.conversation_id === conversationId)) {
        if (n.id) {
          optimisticReadIds.current.add(n.id);
          setTimeout(() => optimisticReadIds.current.delete(n.id), 5000);
        }
        return { ...n, is_read: true };
      }
      return n;
    }));
    // Persist
    if (userIdRef.current) {
      const params = new URLSearchParams();
      if (postId) params.append('post_id', postId);
      if (commentId) params.append('comment_id', commentId);
      if (conversationId) params.append('conversation_id', conversationId);
      if (params.toString()) {
        persistMarkRead(`${API_URL}/api/comms/v3/notifications/mark-read-by-content/${userIdRef.current}?${params}`);
      }
    }
  }, [bumpServerUnread]);

  // ──────────── Sync read state from another device (via WebSocket) ────────────
  // Local-only update — no API call, the originating device already persisted.
  const syncReadFromRemote = useCallback((event) => {
    if (event.scope === 'all') {
      setServerUnread(c => (c === null ? c : 0));
    } else if (event.scope === 'single') {
      const inSlice = notificationsRef.current.find(n => n.id === event.notification_id);
      // Not in the loaded slice (older than 50 rows): the server did decrement, so we do too.
      if (!inSlice || !inSlice.is_read) bumpServerUnread(-1);
    } else if (event.scope === 'content') {
      const matched = notificationsRef.current.filter(n => !n.is_read && (
        (event.post_id && n.post_id === event.post_id) ||
        (event.comment_id && n.comment_id === event.comment_id) ||
        (event.conversation_id && n.conversation_id === event.conversation_id))).length;
      if (matched) bumpServerUnread(-matched);
      else refetch(); // the affected rows may be outside the slice; resync the total
    }
    setNotifications(prev => {
      if (event.scope === 'all') {
        return prev.map(n => ({ ...n, is_read: true }));
      }
      if (event.scope === 'single') {
        return prev.map(n => n.id === event.notification_id ? { ...n, is_read: true } : n);
      }
      if (event.scope === 'content') {
        return prev.map(n => {
          if ((event.post_id && n.post_id === event.post_id) ||
              (event.comment_id && n.comment_id === event.comment_id) ||
              (event.conversation_id && n.conversation_id === event.conversation_id)) {
            return { ...n, is_read: true };
          }
          return n;
        });
      }
      return prev;
    });
  }, [bumpServerUnread, refetch]);

  // ──────────── Add notification (from WebSocket) ────────────
  // "Born as read" — if the DM is for the active conversation, mark it read immediately
  const addNotification = useCallback((notif) => {
    const isDmForActiveConversation =
      (notif.type === 'dm' || notif.type === 'message_reaction') &&
      notif.conversation_id &&
      notif.conversation_id === activeConversationRef.current;

    const enrichedNotif = isDmForActiveConversation
      ? { ...notif, is_read: true }
      : notif;

    const existing = notificationsRef.current.find(n => n.id === enrichedNotif.id);
    if (!existing && !enrichedNotif.is_read) bumpServerUnread(1);
    // An aggregated notification re-opened (read → unread) also counts.
    if (existing && existing.is_read && enrichedNotif.is_read === false) bumpServerUnread(1);

    setNotifications(prev => {
      // Upsert: update existing (e.g. aggregated reaction) or insert new
      const idx = prev.findIndex(n => n.id === enrichedNotif.id);
      const next = idx >= 0
        ? prev.map((n, i) => (i === idx ? { ...n, ...enrichedNotif } : n))
        : [enrichedNotif, ...prev];
      notificationsRef.current = next;
      return next;
    });

    // If born-as-read, persist to DB so it stays read on refetch, and shield
    // it from a refetch that races the persist (would otherwise count it unread).
    if (isDmForActiveConversation && enrichedNotif.id) {
      optimisticReadIds.current.add(enrichedNotif.id);
      setTimeout(() => optimisticReadIds.current.delete(enrichedNotif.id), 5000);
      persistMarkRead(`${API_URL}/api/comms/v3/notifications/${enrichedNotif.id}/read`, userIdRef.current);
    }
  }, [bumpServerUnread]);

  // ──────────── Active conversation tracking ────────────
  // When the user opens a conversation, mark all matching notifications as read
  const setActiveConversation = useCallback((conversationId) => {
    activeConversationRef.current = conversationId;
    if (conversationId) {
      // Mark all existing notifications for this conversation as read
      markReadByTarget({ conversationId });
    }
  }, [markReadByTarget]);

  return {
    notifications,
    unreadCount,
    hasMore,
    loadMore,
    markRead,
    markReadByIds,
    markAllRead,
    markReadByTarget,
    addNotification,
    syncReadFromRemote,
    refetch,
    setActiveConversation,
  };
}
